import { useMutation } from '@apollo/client'
import { jwtDecode } from 'jwt-decode'
import { use, useCallback, useEffect, useState, useTransition } from 'react'
import { Navigate, useSearchParams } from 'react-router'
import { useNavigate } from 'react-router'
import { AuthContext, AuthDispatchContext } from '@/components/auth/context'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { REMEMBER_ME_KEY } from '@/config/local-storage'
import { RECAPTCHA_ACTION_LOGIN } from '@/config/recaptcha'
import { gql } from '@/lib/graphql/gql'
import { useRecaptcha } from '@/lib/hooks'
import { SevallaLogo } from '@/logos'
import { LoginForm } from '@/routes/login/login-form'
import { TwoFAForm } from '@/routes/login/twofa-form'
import type {
  LoginMutation,
  LoginMutationVariables,
} from '@/routes/login/index.types.gen'

const LOGIN_MUTATION = gql`
  mutation Login(
    $email: String!
    $password: String!
    $recaptchaToken: String
    $rememberMeToken: String
  ) {
    login(
      email: $email
      password: $password
      recaptchaToken: $recaptchaToken
      rememberMeToken: $rememberMeToken
    ) {
      token
      error
    }
  }
`

export const Login = () => {
  const [isPending, startTransition] = useTransition()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') || ''
  const oauthErrorCode = searchParams.get('error') || ''
  const rawReturnTo = searchParams.get('returnTo')
  const returnTo = rawReturnTo?.startsWith('/') ? rawReturnTo : null
  const { isAuthenticated = false, twoFA } = use(AuthContext) ?? {}
  const { decodeTwoFAToken, login: saveLoginToken } =
    use(AuthDispatchContext) ?? {}

  const oauthErrors = {
    USER_NOT_FOUND_BY_GIT_SERVICE_ID: (
      <div>
        No Sevalla account found for this Git user. Please{' '}
        <a
          href="https://sevalla.com/signup/"
          className="underline underline-offset-4"
        >
          sign up
        </a>{' '}
        for a new account or log in using your email and password, then link
        your Git account in the Sevalla dashboard under User Settings.
      </div>
    ),
  }

  const oauthError = oauthErrorCode
    ? Object.hasOwn(oauthErrors, oauthErrorCode)
      ? (oauthErrors[oauthErrorCode as keyof typeof oauthErrors] ??
        'Unexpected error. Please try it later or contact the support.')
      : ''
    : undefined

  const [loginState, setLoginState] = useState<{
    email: string
    password: string
    twoFAToken: string
  }>({
    email: '',
    password: '',
    twoFAToken: '',
  })
  const getRecaptchaToken = useRecaptcha()
  const [login, { data: mutationData, error }] = useMutation<
    LoginMutation,
    LoginMutationVariables
  >(LOGIN_MUTATION)

  const loginOrRequestTwoFA = useCallback(
    async ({
      email,
      password,
    }: {
      email: string
      password: string
    }): Promise<'authenticated' | 'emailTwoFA' | 'totp' | null> => {
      const recaptchaToken = await getRecaptchaToken(RECAPTCHA_ACTION_LOGIN)
      if (!recaptchaToken) {
        return null
      }

      const { data } = await login({
        variables: {
          email,
          password,
          recaptchaToken,
          rememberMeToken: localStorage.getItem(REMEMBER_ME_KEY) ?? null,
        },
      })

      setLoginState((state) => ({
        ...state,
        email,
        password,
      }))

      const returnedToken = data?.login?.token ?? ''

      try {
        const decodedToken = jwtDecode<
          | { type: 'authenticated' }
          | { twoFA: Record<string, string | boolean> }
        >(returnedToken)

        if ('twoFA' in decodedToken) {
          decodeTwoFAToken?.(returnedToken)
          setLoginState((state) => ({
            ...state,
            twoFAToken: returnedToken,
          }))
          return decodedToken.twoFA.type === 'email' ||
            decodedToken.twoFA.type === 'registration'
            ? 'emailTwoFA'
            : 'totp'
        } else if (decodedToken.type === 'authenticated') {
          saveLoginToken?.(returnedToken)
          return 'authenticated'
        }
      } catch (err) {
        console.error('Error decoding token:', err)
        return null
      }
      return null
    },
    [decodeTwoFAToken, getRecaptchaToken, login, saveLoginToken]
  )

  const onSubmit = useCallback(
    ({ email, password }: { email: string; password: string }) => {
      startTransition(async () => {
        const result = await loginOrRequestTwoFA({ email, password })

        if (result === 'authenticated') {
          navigate(returnTo ?? '/')
        }
      })
    },
    [loginOrRequestTwoFA, navigate, returnTo]
  )

  useEffect(() => {
    if (token && saveLoginToken) {
      try {
        jwtDecode(token)
        saveLoginToken?.(token)
        navigate(returnTo ?? '/', { replace: true })
      } catch (err) {
        navigate('/login', { replace: true })
        console.error('Error decoding token:', err)
      }
    }
  }, [token, saveLoginToken, navigate, returnTo])

  if (isAuthenticated) {
    return <Navigate to={returnTo ?? '/'} replace />
  }

  return (
    <div className="bg-muted flex min-h-svh flex-col items-center justify-center gap-6 p-6 md:p-10">
      <div className="flex w-full max-w-sm flex-col gap-4">
        <a
          href="https://sevalla.com"
          className="flex items-center gap-2 self-center"
        >
          <SevallaLogo className="w-24 h-auto" />
        </a>
        <div className="flex flex-col gap-6">
          <Card>
            <CardHeader className="text-center">
              <CardTitle className="font-display text-xl">
                Welcome to Sevalla
              </CardTitle>
              <CardDescription>
                {twoFA?.type === 'email' || twoFA?.type === 'registration'
                  ? `Enter the code from the email we've just sent you.
`
                  : twoFA?.type === 'totp'
                    ? 'Enter the code from your authentication app.'
                    : 'Nice to see you again!'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {['registration', 'email', 'totp'].includes(twoFA?.type ?? '') ? (
                <TwoFAForm
                  requestNewEmailCode={loginOrRequestTwoFA}
                  loginState={loginState}
                  returnTo={returnTo}
                />
              ) : (
                <LoginForm
                  isPending={isPending}
                  onSubmit={onSubmit}
                  error={
                    mutationData?.login?.error ?? error?.message ?? oauthError
                  }
                />
              )}
              <div className="mt-4 text-muted-foreground *:[a]:hover:text-primary text-center text-xs text-balance *:[a]:underline *:[a]:underline-offset-4">
                This site is protected by reCAPTCHA and the Google{' '}
                <a href="https://policies.google.com/privacy" target="_blank">
                  Privacy Policy
                </a>{' '}
                and{' '}
                <a href="https://policies.google.com/terms" target="_blank">
                  Terms of Service
                </a>{' '}
                apply.
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
